const CATALOG_ROUTE = 'catalog'
const CATALOG_SUBTHEMES_ROUTE = 'subthemes'

const safeDecodeURIComponent = (value: string): string => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

export const CATALOG_COLLECTION_TYPE_TO_LENS: Record<string, string> = {
  brands: 'brand',
  brand: 'brand',
  themes: 'theme',
  theme: 'theme',
  sources: 'source_type',
  source: 'source_type',
  source_type: 'source_type',
  products: 'product_hierarchy',
  product: 'product_hierarchy',
  product_hierarchy: 'product_hierarchy',
}

export const getCatalogLensFromCollectionType = (
  collectionType: string | null | undefined,
): string | null => {
  if (!collectionType) {
    return null
  }

  const normalized = safeDecodeURIComponent(collectionType).toLowerCase()
  return CATALOG_COLLECTION_TYPE_TO_LENS[normalized] ?? null
}

export const getCatalogCollectionTypeFromLens = (lens: string): string => {
  switch (lens) {
    case 'brand':
      return 'brands'
    case 'theme':
      return 'themes'
    case 'source_type':
      return 'sources'
    case 'product_hierarchy':
      return 'products'
    default:
      return lens
  }
}

export const buildCatalogPath = (
  collectionType: string,
  itemName?: string | null,
  subthemeName?: string | null,
): string => {
  const normalizedCollectionType = encodeURIComponent(
    safeDecodeURIComponent(collectionType).toLowerCase(),
  )
  if (!itemName) {
    return `${CATALOG_ROUTE}/${normalizedCollectionType}`
  }
  const normalizedItemName = safeDecodeURIComponent(itemName).toLowerCase()
  if (!subthemeName) {
    return `${CATALOG_ROUTE}/${normalizedCollectionType}/${encodeURIComponent(normalizedItemName)}`
  }
  const normalizedSubthemeName =
    safeDecodeURIComponent(subthemeName).toLowerCase()
  const encodedItem = encodeURIComponent(normalizedItemName)
  const encodedSubtheme = encodeURIComponent(normalizedSubthemeName)
  return `${CATALOG_ROUTE}/${normalizedCollectionType}/${encodedItem}/${CATALOG_SUBTHEMES_ROUTE}/${encodedSubtheme}`
}

export const buildCatalogPathFromLens = (
  lens: string,
  itemName?: string | null,
  subthemeName?: string | null,
): string =>
  buildCatalogPath(
    getCatalogCollectionTypeFromLens(lens),
    itemName,
    subthemeName,
  )

export const buildProjectCatalogPath = (
  projectId: string,
  collectionType: string,
  itemName?: string | null,
  subthemeName?: string | null,
): string =>
  `/project/${projectId}/${buildCatalogPath(collectionType, itemName, subthemeName)}`

export const buildProjectCatalogPathFromLens = (
  projectId: string,
  lens: string,
  itemName?: string | null,
  subthemeName?: string | null,
): string =>
  `/project/${projectId}/${buildCatalogPathFromLens(lens, itemName, subthemeName)}`

export const getCatalogRedirectFromLegacyQuery = (
  projectId: string | null | undefined,
  collectionType: string | null | undefined,
  search: string,
  itemName?: string | null,
  subthemeName?: string | null,
): string | null => {
  if (!projectId) {
    return null
  }

  const urlParams = new URLSearchParams(search)
  const legacyCategory = urlParams.get('category')
  const legacyItem = urlParams.get('item')
  const legacySubtheme = urlParams.get('subtheme')

  if (!collectionType) {
    if (!legacyCategory && !legacyItem) {
      return null
    }

    const lensFromCategory = getCatalogLensFromCollectionType(legacyCategory)
    if (legacyCategory && !lensFromCategory) {
      return null
    }

    const lens = lensFromCategory ?? 'product_hierarchy'
    const nextPath = buildProjectCatalogPathFromLens(
      projectId,
      lens,
      legacyItem,
      lens === 'theme' ? legacySubtheme : null,
    )

    urlParams.delete('category')
    urlParams.delete('item')
    urlParams.delete('subtheme')
    const remainingParams = urlParams.toString()

    return `${nextPath}${remainingParams ? `?${remainingParams}` : ''}`
  }

  const lensFromPath = getCatalogLensFromCollectionType(collectionType)
  if (
    legacySubtheme &&
    !subthemeName &&
    lensFromPath === 'theme' &&
    Boolean(itemName)
  ) {
    const nextPath = buildProjectCatalogPathFromLens(
      projectId,
      lensFromPath,
      itemName ?? null,
      legacySubtheme,
    )
    urlParams.delete('category')
    urlParams.delete('item')
    urlParams.delete('subtheme')
    const remainingParams = urlParams.toString()
    return `${nextPath}${remainingParams ? `?${remainingParams}` : ''}`
  }

  return null
}
